import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Activity, AlertCircle, ChevronLeft, ChevronRight, Download, History, KeyRound, ShieldAlert, Terminal } from 'lucide-react';
import { cn } from '../lib/utils';
import { Card, Disclosure, EmptyState, ICON, Kpi, Notice, PageHeader, Skeleton, Tabs } from '../components/ui';

const BASE = (import.meta as any).env?.VITE_API_BASE_URL ?? '';

// Low-level fetch helpers — Admin page praat direct met de Worker zodat we
// geen cross-cutting wijzigingen in app/src/lib/api.ts hoeven te maken.
async function adminGet<T>(path: string): Promise<T> {
  const r = await fetch(`${BASE}${path}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json();
}
async function adminPost<T>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json();
}

interface AuditRow {
  id: number;
  ts: string;
  user_email: string | null;
  method: string;
  path: string;
  query: string | null;
  row_ids_affected: string | null;
  status: number | null;
  body_hash: string | null;
  duration_ms: number | null;
  ip: string | null;
  user_agent: string | null;
}
interface AuditStats {
  last24h: number;
  last7d: number;
  errors24h: number;
  topPaths: Array<{ path: string; c: number }>;
}

/** "2026-10-23T14:05:09Z" -> "23-10-2026 14:05:09" (tijd blijft zoals opgeslagen). */
function fmtTs(ts: string): string {
  const m = ts.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2}(?::\d{2})?)/);
  return m ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}` : ts;
}

const nl = (n: number) => Math.round(n).toLocaleString('nl-NL');

type Section = 'export' | 'audit' | 'rotate';

export function AdminPage() {
  const [section, setSection] = useState<Section>('export');

  return (
    <div className="page">
      <PageHeader title="Beheer" sub="Backup, activiteit en sleutels." />
      <Tabs<Section>
        items={[
          { value: 'export', label: 'Backup' },
          { value: 'audit', label: 'Activiteit' },
          { value: 'rotate', label: 'Sleutels' },
        ]}
        value={section}
        onChange={setSection}
      />

      {section === 'export' && <ExportSection />}
      {section === 'audit' && <AuditSection />}
      {section === 'rotate' && <RotateSection />}
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-lg border border-surface-line/60 bg-slate-950/60 p-3 font-mono text-xs text-slate-300">{children}</pre>
  );
}

// ---------------------------------------------------------------------------
function ExportSection() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setError(null);
    setDownloading(true);
    try {
      const r = await fetch(`${BASE}/api/admin/export-all`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
      const blob = await r.blob();
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `trigion-app-export-${ts}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card title="Volledige backup" icon={Download}>
      <div className="space-y-4">
        <p className="t-muted">Een ZIP met al je gegevens en instellingen, zonder wachtwoorden en sleutels. Opgeslagen PDF's zitten er niet in.</p>
        <div className="flex justify-end">
          <button onClick={download} disabled={downloading} className="btn-primary">
            <Download className={ICON.sm} aria-hidden />
            {downloading ? 'Backup maken…' : 'Backup downloaden'}
          </button>
        </div>
        {error && (
          <Notice tone="err">
            <span className="break-all">{error}</span>
          </Notice>
        )}
      </div>

      <Disclosure title="Backup en herstel via de terminal" icon={Terminal} className="-mx-4 -mb-4 mt-4 border-t border-surface-line/60 sm:-mx-5 sm:-mb-5">
        <CodeBlock>
          {`# 1. Cookie uit browser kopiëren (DevTools > Application > Cookies)
export TRIGION_COOKIE="session_token=<jwt>"
npx tsx scripts/backup-full.ts

# 2. Herstel in een lege database:
bash scripts/restore.sh data/backups/trigion-app-export-<ts>.zip`}
        </CodeBlock>
      </Disclosure>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function AuditSection() {
  const [filter, setFilter] = useState({ method: '', path: '', status: '' });
  const [page, setPage] = useState(0);
  const limit = 100;

  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  qs.set('offset', String(page * limit));
  if (filter.method) qs.set('method', filter.method);
  if (filter.path) qs.set('path', filter.path);
  if (filter.status) qs.set('status', filter.status);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['audit', filter, page],
    queryFn: () => adminGet<{ total: number; rows: AuditRow[] }>(`/api/admin/audit?${qs}`),
  });
  const stats = useQuery({
    queryKey: ['audit-stats'],
    queryFn: () => adminGet<AuditStats>('/api/admin/audit/stats'),
  });

  const cleanup = useMutation({
    mutationFn: () => adminPost<{ ok: true; rows_deleted: number }>('/api/admin/audit/cleanup?days=90'),
    onSuccess: () => {
      refetch();
      stats.refetch();
    },
  });

  return (
    <>
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <Kpi label="Verzoeken 24 uur" value={stats.data?.last24h} format={nl} />
        <Kpi label="Verzoeken 7 dagen" value={stats.data?.last7d} format={nl} />
        <Kpi label="Fouten 24 uur" value={stats.data?.errors24h} format={nl} tone={stats.data && stats.data.errors24h > 0 ? 'err' : 'default'} />
      </div>

      <Card
        flush
        title="Activiteit"
        icon={Activity}
        action={
          <button onClick={() => cleanup.mutate()} disabled={cleanup.isPending} className="btn-ghost">
            {cleanup.isPending ? 'Bezig…' : 'Ouder dan 90 dagen wissen'}
          </button>
        }
      >
        <div className="grid grid-cols-2 gap-3 px-4 pb-4 sm:grid-cols-[8rem_7rem_minmax(0,1fr)] sm:px-5">
          <div>
            <label className="label" htmlFor="audit-method">
              Methode
            </label>
            <select
              id="audit-method"
              className="input"
              value={filter.method}
              onChange={(e) => {
                setFilter({ ...filter, method: e.target.value });
                setPage(0);
              }}
            >
              <option value="">Alle</option>
              {['GET', 'POST', 'PATCH', 'DELETE'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="audit-status">
              Status
            </label>
            <input
              id="audit-status"
              className="input"
              value={filter.status}
              onChange={(e) => {
                setFilter({ ...filter, status: e.target.value });
                setPage(0);
              }}
              placeholder="200"
              inputMode="numeric"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="label" htmlFor="audit-path">
              Adres bevat
            </label>
            <input
              id="audit-path"
              className="input"
              value={filter.path}
              onChange={(e) => {
                setFilter({ ...filter, path: e.target.value });
                setPage(0);
              }}
              placeholder="/api/…"
            />
          </div>
        </div>

        {cleanup.data && (
          <div className="px-4 pb-4 sm:px-5">
            <Notice tone="ok">{cleanup.data.rows_deleted} regels gewist.</Notice>
          </div>
        )}

        <div className="border-t border-surface-line">
          {isLoading ? (
            <div className="space-y-2 p-4 sm:p-5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : !data || data.rows.length === 0 ? (
            <EmptyState icon={Activity} title="Geen resultaten" text="Pas de filters aan." />
          ) : (
            <div className="overflow-x-auto">
              <table className="table min-w-[680px]">
                <thead>
                  <tr>
                    <th>Tijd (UTC)</th>
                    <th>Gebruiker</th>
                    <th>Methode</th>
                    <th>Adres</th>
                    <th className="r">Status</th>
                    <th className="r">Duur (ms)</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className="text-slate-300">
                      <td className="t-num whitespace-nowrap">{fmtTs(r.ts)}</td>
                      <td>{r.user_email?.split('@')[0] ?? '–'}</td>
                      <td className="font-mono text-xs">{r.method}</td>
                      <td className="max-w-[26rem] truncate font-mono text-xs" title={r.path + (r.query ? `?${r.query}` : '')}>
                        {r.path}
                      </td>
                      <td className={cn('r', r.status && r.status >= 400 && 'text-red-300')}>{r.status ?? '–'}</td>
                      <td className="r">{r.duration_ms ?? '–'}</td>
                      <td className="font-mono text-xs text-slate-500">{r.ip ?? '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {data && (
          <div className="flex items-center justify-between gap-3 border-t border-surface-line px-4 py-2 text-sm text-slate-400 sm:px-5">
            <span className="t-num">
              {data.total.toLocaleString('nl-NL')} regels, pagina {page + 1}
            </span>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="btn-ghost">
                <ChevronLeft className={ICON.sm} aria-hidden /> Vorige
              </button>
              <button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= data.total} className="btn-ghost">
                Volgende <ChevronRight className={ICON.sm} aria-hidden />
              </button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
function RotateSection() {
  const [sessionResult, setSessionResult] = useState<{ new_secret: string; grace_until: string } | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [saltInput, setSaltInput] = useState('');
  const [saltResult, setSaltResult] = useState<{ rows_updated: number } | null>(null);
  const [saltError, setSaltError] = useState<string | null>(null);
  const [showDanger, setShowDanger] = useState(false);

  const rotateSession = useMutation({
    mutationFn: () => adminPost<{ new_secret: string; grace_until: string }>('/api/admin/rotate-session-secret'),
    onSuccess: (d) => {
      setSessionResult(d);
      setSessionError(null);
    },
    onError: (e) => {
      setSessionError((e as Error).message);
      setSessionResult(null);
    },
  });

  const rotateSalt = useMutation({
    mutationFn: () =>
      adminPost<{ rows_updated: number }>('/api/admin/rotate-encryption-salt', {
        new_salt: saltInput,
        confirm: 'JA-IK-WEET-HET',
      }),
    onSuccess: (d) => {
      setSaltResult(d);
      setSaltError(null);
    },
    onError: (e) => {
      setSaltError((e as Error).message);
      setSaltResult(null);
    },
  });

  const rotationLog = useQuery({
    queryKey: ['secret-rotation-log'],
    queryFn: () =>
      adminGet<{ rows: Array<{ id: number; secret_name: string; rotated_at: string; performed_by: string | null; notes: string }> }>(
        '/api/admin/secret-rotation-log'
      ),
  });

  const generateSalt = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(48));
    let b = '';
    for (const x of bytes) b += String.fromCharCode(x);
    const b64 = btoa(b).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    setSaltInput(b64);
  };

  return (
    <>
      <Card title="Inlogsleutel vernieuwen" icon={KeyRound}>
        <div className="space-y-4">
          <p className="t-muted">De nieuwe sleutel wordt één keer getoond. Zet hem meteen in de server-instellingen; daarna log je opnieuw in.</p>
          <div className="flex justify-end">
            <button onClick={() => rotateSession.mutate()} disabled={rotateSession.isPending} className="btn-secondary">
              {rotateSession.isPending ? 'Bezig…' : 'Nieuwe inlogsleutel maken'}
            </button>
          </div>
          {sessionError && (
            <Notice tone="err">
              <span className="break-all">{sessionError}</span>
            </Notice>
          )}
          {sessionResult && (
            <div className="space-y-3 border-t border-surface-line/60 pt-4 text-sm">
              <Notice tone="warn">Kopieer hem nu, hij wordt niet nog eens getoond.</Notice>
              <code className="block break-all rounded-lg border border-surface-line/60 bg-slate-950/60 p-3 font-mono text-xs text-slate-100">
                {sessionResult.new_secret}
              </code>
              <p className="t-muted">De oude sleutel werkt nog tot {fmtTs(sessionResult.grace_until)}.</p>
              <CodeBlock>{`echo "${sessionResult.new_secret}" | wrangler secret put SESSION_SECRET`}</CodeBlock>
            </div>
          )}
        </div>
      </Card>

      <Card title="Versleutelingssleutel vernieuwen" icon={ShieldAlert}>
        <div className="space-y-4">
          <p className="t-muted">
            Versleutelt je gevoelige gegevens (BSN, telefoon, adres, agendakoppeling) opnieuw. Zet de nieuwe sleutel direct daarna in de
            server-instellingen, anders zijn die gegevens onleesbaar.
          </p>

          {!showDanger ? (
            <div className="flex justify-end">
              <button onClick={() => setShowDanger(true)} className="btn-secondary">
                Doorgaan
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="label" htmlFor="new-salt">
                  Nieuwe sleutel (minstens 32 tekens)
                </label>
                <div className="flex gap-2">
                  <input
                    id="new-salt"
                    className="input min-w-0 flex-1 font-mono text-xs"
                    value={saltInput}
                    onChange={(e) => setSaltInput(e.target.value)}
                    placeholder="Plakken of laten maken"
                  />
                  <button onClick={generateSalt} className="btn-secondary shrink-0">
                    Maken
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  onClick={() => {
                    setShowDanger(false);
                    setSaltInput('');
                    setSaltResult(null);
                    setSaltError(null);
                  }}
                  className="btn-ghost"
                >
                  Annuleren
                </button>
                <button onClick={() => rotateSalt.mutate()} disabled={saltInput.length < 32 || rotateSalt.isPending} className="btn-danger">
                  {rotateSalt.isPending ? 'Opnieuw versleutelen…' : 'Alles opnieuw versleutelen'}
                </button>
              </div>
              {saltError && (
                <Notice tone="err">
                  <span className="break-all">{saltError}</span>
                </Notice>
              )}
              {saltResult && (
                <div className="space-y-2">
                  <Notice tone="ok">{saltResult.rows_updated} rij(en) opnieuw versleuteld. Zet nu de nieuwe sleutel:</Notice>
                  <CodeBlock>{`echo "${saltInput}" | wrangler secret put ENCRYPTION_SALT
wrangler deploy`}</CodeBlock>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card flush title="Eerder vernieuwd" icon={History}>
        <div className="border-t border-surface-line">
          {rotationLog.isLoading ? (
            <div className="space-y-2 p-4 sm:p-5">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : rotationLog.isError ? (
            <EmptyState icon={AlertCircle} title="Kon de historie niet laden" text={(rotationLog.error as Error).message} />
          ) : !rotationLog.data || rotationLog.data.rows.length === 0 ? (
            <EmptyState icon={History} title="Nog geen sleutels vernieuwd" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table min-w-[520px]">
                <thead>
                  <tr>
                    <th>Tijd</th>
                    <th>Sleutel</th>
                    <th>Door</th>
                    <th>Notitie</th>
                  </tr>
                </thead>
                <tbody>
                  {rotationLog.data.rows.map((r) => (
                    <tr key={r.id} className="text-slate-300">
                      <td className="t-num whitespace-nowrap">{fmtTs(r.rotated_at)}</td>
                      <td className="font-mono text-xs">{r.secret_name}</td>
                      <td>{r.performed_by ?? '–'}</td>
                      <td className="text-slate-400">{r.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
